// src/pages/hr/recruitment/CandidateProfile.jsx
import React, { useState } from "react";
import { updateCandidateStage, createOffer, assignCandidateToLead, submitLeadRecommendation } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import { UserCheck, ShieldCheck, ThumbsUp, ThumbsDown, Clock, CheckCircle2, AlertTriangle, Calendar, FileText } from "lucide-react";

export default function CandidateProfile({ candidate, onClose, onRefresh }) {
  const { userProfile } = useAuth();
  const role = (userProfile?.role || "").toLowerCase();
  const isHR = role === "hr" || role === "admin";
  const isLead = role === "lead";

  const [activeTab, setActiveTab] = useState("overview");

  // Assignment Modal State
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [leadNameInput, setLeadNameInput] = useState(candidate?.assignedLeadName || "");

  // Team Lead Recommendation Modal State
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadRecInput, setLeadRecInput] = useState("Proceed to Technical Interview");
  const [leadCommentsInput, setLeadCommentsInput] = useState("");

  // Offer form
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerData, setOfferData] = useState({
    salary: "",
    joiningDate: "",
    reportingManager: "",
    benefits: "Health Insurance, Paid Leaves, Performance Bonus",
    notes: "",
  });

  if (!candidate) return null;

  async function handleStageChange(newStage) {
    await updateCandidateStage(candidate.id, newStage, userProfile?.displayName || "HR Admin", candidate.jobId);
    if (onRefresh) onRefresh();
  }

  async function handleAssignSubmit(e) {
    e.preventDefault();
    if (!leadNameInput) return;
    await assignCandidateToLead(candidate.id, "lead_id_01", leadNameInput, userProfile?.displayName || "HR Admin");
    setShowAssignModal(false);
    alert(`Candidate assigned to Team Lead: ${leadNameInput}`);
    if (onRefresh) onRefresh();
  }

  async function handleLeadRecSubmit(e) {
    e.preventDefault();
    await submitLeadRecommendation(candidate.id, leadRecInput, leadCommentsInput, userProfile?.displayName || "Team Lead");
    setShowLeadModal(false);
    alert(`Recommendation saved: "${leadRecInput}". HR notified for interview scheduling.`);
    if (onRefresh) onRefresh();
  }

  async function handleGenerateOffer(e) {
    e.preventDefault();
    await createOffer({
      candidateId: candidate.id,
      candidateName: candidate.name,
      candidateEmail: candidate.email,
      jobId: candidate.jobId || "",
      jobTitle: candidate.appliedPosition || candidate.role || "",
      ...offerData,
      createdByName: userProfile?.displayName || "HR",
    });
    await updateCandidateStage(candidate.id, "Offer", userProfile?.displayName || "HR", candidate.jobId);
    setShowOfferModal(false);
    alert("Offer generated successfully!");
    if (onRefresh) onRefresh();
  }

  return (
    <div className="modal-overlay">
      <style>{`
        .prof-modal { background: var(--bg-card, #fff); border-radius: 14px; width: 100%; max-width: 900px; max-height: 92vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 10px 40px rgba(0,0,0,0.25); }
        .prof-header { background: linear-gradient(135deg, #1b4b31, #2d7a50); color: #fff; padding: 1.25rem 1.5rem; display: flex; justify-content: space-between; align-items: flex-start; }
        .prof-avatar { width: 56px; height: 56px; border-radius: 50%; background: #fff; color: #1b4b31; font-weight: 800; font-size: 1.3rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .prof-nav { display: flex; gap: 0.5rem; background: var(--bg-secondary, #f9fafb); padding: 0.5rem 1.5rem; border-bottom: 1px solid var(--border, #e5e7eb); overflow-x: auto; }
        .prof-tab { padding: 0.5rem 1rem; border: none; background: none; font-size: 0.83rem; font-weight: 600; cursor: pointer; color: var(--text-muted); border-bottom: 3px solid transparent; white-space: nowrap; }
        .prof-tab.active { color: #1b4b31; border-bottom-color: #1b4b31; }
        .prof-body { padding: 1.5rem; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 1rem; }
        .prof-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media(max-width: 650px) { .prof-grid-2 { grid-template-columns: 1fr; } }
        .prof-item { display: flex; justify-content: space-between; padding: 0.45rem 0; border-bottom: 1px solid var(--border, #f3f4f6); font-size: 0.82rem; }
        .prof-lbl { color: var(--text-muted); }
        .prof-val { font-weight: 600; color: var(--text-primary, #111); text-align: right; }
        .timeline-list { display: flex; flex-direction: column; gap: 0.75rem; border-left: 2px solid #1b4b31; padding-left: 1rem; margin-left: 0.5rem; }
        .timeline-item { position: relative; }
        .timeline-item::before { content: ""; position: absolute; left: -1.35rem; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: #1b4b31; }
        
        .rec-box {
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
        }
        .rec-box--proceed { background: #f0fdf4; border-color: #bbf7d0; }
        .rec-box--reject  { background: #fff1f2; border-color: #fecdd3; }
        .rec-box--hold    { background: #fffbeb; border-color: #fde68a; }
      `}</style>

      <div className="prof-modal">
        {/* Header */}
        <div className="prof-header">
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <div className="prof-avatar">
              {candidate.name?.slice(0, 2)?.toUpperCase() || "C"}
            </div>
            <div>
              <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>{candidate.name}</div>
              <div style={{ fontSize: "0.8rem", opacity: 0.85 }}>
                {candidate.appliedPosition || candidate.role || "Applicant"} · {candidate.email || "No email"}
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ background: "rgba(255,255,255,0.2)", padding: "0.15rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>
                  Stage: {candidate.stage || "Applied"}
                </span>
                {candidate.leadRecommendation && (
                  <span style={{ background: candidate.leadRecommendation === "Proceed to Technical Interview" ? "#10b981" : "#f59e0b", color: "#fff", padding: "0.15rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>
                    Lead: {candidate.leadRecommendation}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="btn-secondary" style={{ padding: "0.3rem 0.6rem", color: "#fff", background: "rgba(255,255,255,0.2)", border: "none" }} onClick={onClose}>
            ✕ Close
          </button>
        </div>

        {/* Sub-nav */}
        <div className="prof-nav">
          {[
            { id: "overview", label: "👤 Overview & Screening" },
            { id: "leadReview", label: "👨‍💼 Team Lead Recommendation" },
            { id: "resume", label: "📄 Resume & Skills" },
            { id: "timeline", label: "⏱️ Timeline" },
            { id: "offer", label: "💼 Offer & Hiring" },
          ].map((t) => (
            <button key={t.id} className={`prof-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="prof-body">
          {activeTab === "overview" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>Candidate Profile Details</h4>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {isHR && (
                    <button className="btn-secondary" style={{ padding: "0.3rem 0.75rem", fontSize: "0.78rem" }} onClick={() => setShowAssignModal(true)}>
                      👨‍💼 Assign to Team Lead
                    </button>
                  )}
                  <select
                    className="ats-select"
                    style={{ fontSize: "0.78rem" }}
                    value={candidate.stage}
                    onChange={(e) => handleStageChange(e.target.value)}
                  >
                    {[
                      "Applied", "Screening", "Interview Scheduled", "Interview Completed",
                      "Manager Review", "HR Review", "Offer", "Hired", "Rejected", "Hold"
                    ].map((s) => (
                      <option key={s} value={s}>Stage: {s}</option>
                    ))}
                  </select>
                  <button className="btn-primary" style={{ padding: "0.3rem 0.75rem", fontSize: "0.78rem" }} onClick={() => setShowOfferModal(true)}>
                    📝 Generate Offer
                  </button>
                </div>
              </div>

              {/* Team Lead Recommendation Banner inside Overview */}
              {candidate.leadRecommendation ? (
                <div className={`rec-box ${candidate.leadRecommendation === "Proceed to Technical Interview" ? "rec-box--proceed" : candidate.leadRecommendation === "Reject" ? "rec-box--reject" : "rec-box--hold"}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: "0.9rem", color: candidate.leadRecommendation === "Proceed to Technical Interview" ? "#065f46" : "#991b1b" }}>
                        Team Lead Recommendation: {candidate.leadRecommendation}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 2 }}>
                        Evaluated by <b>{candidate.leadReviewedBy || candidate.assignedLeadName || "Team Lead"}</b>
                        {candidate.leadComments && ` — "${candidate.leadComments}"`}
                      </div>
                    </div>
                    {isHR && candidate.leadRecommendation === "Proceed to Technical Interview" && (
                      <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#166534", background: "#bbf7d0", padding: "0.25rem 0.65rem", borderRadius: 99 }}>
                        Ready for HR Scheduling
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", padding: "0.75rem 1rem", borderRadius: 10, fontSize: "0.82rem", color: "#92400e", marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>
                    <b>Assigned Lead:</b> {candidate.assignedLeadName || "Not assigned yet"} — Awaiting Team Lead resume evaluation.
                  </span>
                  {isHR && (
                    <button className="btn-secondary" style={{ padding: "0.25rem 0.6rem", fontSize: "0.72rem" }} onClick={() => setShowAssignModal(true)}>
                      Assign Now
                    </button>
                  )}
                </div>
              )}

              <div className="prof-grid-2">
                <div>
                  <div className="prof-item"><span className="prof-lbl">Phone</span><span className="prof-val">{candidate.phone || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Email</span><span className="prof-val">{candidate.email || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Current Company</span><span className="prof-val">{candidate.currentCompany || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Experience</span><span className="prof-val">{candidate.experience || "—"}</span></div>
                </div>
                <div>
                  <div className="prof-item">
                    <span className="prof-lbl">Portfolio / LinkedIn</span>
                    <span className="prof-val">
                      {candidate.portfolioUrl ? (
                        <a href={candidate.portfolioUrl.startsWith("http") ? candidate.portfolioUrl : `https://${candidate.portfolioUrl}`} target="_blank" rel="noreferrer" style={{ color: "#0284c7", fontWeight: 700, textDecoration: "underline" }}>
                          View Link ↗
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <div className="prof-item"><span className="prof-lbl">Source</span><span className="prof-val">{candidate.source || "Website Careers"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Notice Period</span><span className="prof-val">{candidate.noticePeriod || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Recruiter</span><span className="prof-val">{candidate.createdByName || "Online Application"}</span></div>
                </div>
              </div>

              {/* Why do you want to join us / Cover Note */}
              {(candidate.coverLetter || candidate.whyJoinUs) && (
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "1rem 1.15rem", marginTop: "1rem" }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#1b4b31", marginBottom: "0.35rem" }}>
                    💬 Why do you want to join us?
                  </div>
                  <div style={{ fontSize: "0.84rem", color: "#334155", fontStyle: "italic", lineHeight: 1.45 }}>
                    "{candidate.coverLetter || candidate.whyJoinUs}"
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "leadReview" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>Team Lead Resume Evaluation & Recommendation</h4>
                {(isLead || isHR) && (
                  <button className="btn-primary" style={{ padding: "0.35rem 0.85rem", fontSize: "0.78rem" }} onClick={() => setShowLeadModal(true)}>
                    ✏️ Submit Lead Recommendation
                  </button>
                )}
              </div>

              <div style={{ background: "var(--bg-secondary, #f8fafc)", border: "1px solid var(--border, #e2e8f0)", padding: "1.25rem", borderRadius: 12 }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 8 }}>
                  Assigned Team Lead: <span style={{ color: "#1b4b31" }}>{candidate.assignedLeadName || "Unassigned"}</span>
                </div>
                {candidate.leadRecommendation ? (
                  <div style={{ display: "grid", gap: "0.5rem" }}>
                    <div style={{ fontSize: "0.82rem" }}>
                      <b>Recommendation:</b> <span style={{ fontWeight: 800, color: "#166534" }}>{candidate.leadRecommendation}</span>
                    </div>
                    <div style={{ fontSize: "0.82rem" }}>
                      <b>Reviewer Comments:</b> "{candidate.leadComments || "No notes provided."}"
                    </div>
                    <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 4 }}>
                      Reviewed by {candidate.leadReviewedBy} on {new Date(candidate.leadReviewedAt || Date.now()).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>
                    No recommendation submitted yet. The Team Lead reviews the candidate's resume and selects whether to proceed to technical interview, hold, or reject.
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "resume" && (
            <div>
              <h4 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Skills & Experience Profile</h4>
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>Key Skills:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                  {(candidate.skills || ["React", "JavaScript", "HTML/CSS"]).map((sk) => (
                    <span key={sk} style={{ background: "#d1fae5", color: "#065f46", padding: "0.2rem 0.6rem", borderRadius: 99, fontSize: "0.75rem", fontWeight: 600 }}>
                      {sk}
                    </span>
                  ))}
                </div>
              </div>
              {candidate.resumeUrl ? (
                <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "1rem 1.25rem", marginTop: "0.5rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      📄
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#065f46", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {candidate.resumeFileName || "Candidate Resume"}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#166534", marginTop: 2, display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                        {candidate.resumeUploadType === "link" ? (
                          <span>🔗 External Link (Google Drive / OneDrive / Dropbox)</span>
                        ) : (
                          <span>☁️ Firebase Storage</span>
                        )}
                        {candidate.resumeUploadedAt && (
                          <span>📅 {new Date(candidate.resumeUploadedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        )}
                        {candidate.resumeStoragePath && (
                          <span style={{ fontFamily: "monospace", fontSize: "0.66rem", color: "#4b5563" }}>{candidate.resumeStoragePath}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.6rem", marginTop: "0.85rem" }}>
                    <a
                      href={candidate.resumeUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "0.35rem",
                        padding: "0.45rem 1rem", borderRadius: 8, fontSize: "0.82rem", fontWeight: 700,
                        background: "#1b4b31", color: "#fff", textDecoration: "none",
                        border: "none", cursor: "pointer",
                      }}
                    >
                      👁 View Resume
                    </a>
                    <a
                      href={candidate.resumeUrl}
                      download={candidate.resumeFileName || "resume"}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "0.35rem",
                        padding: "0.45rem 1rem", borderRadius: 8, fontSize: "0.82rem", fontWeight: 700,
                        background: "#fff", color: "#1b4b31", textDecoration: "none",
                        border: "1.5px solid #1b4b31", cursor: "pointer",
                      }}
                    >
                      ⬇ Download
                    </a>
                  </div>
                </div>
              ) : (
                <div style={{ background: "var(--bg-secondary)", padding: "1.5rem", borderRadius: 8, fontSize: "0.82rem", color: "var(--text-muted)", textAlign: "center" }}>
                  No resume uploaded. Ask the candidate to provide one or update the profile.
                </div>
              )}
            </div>
          )}

          {activeTab === "timeline" && (
            <div>
              <h4 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Hiring Stage Movement Timeline</h4>
              <div className="timeline-list">
                {(candidate.stageHistory || [{ stage: "Applied", movedAt: new Date().toISOString(), movedBy: "System" }]).map((h, idx) => (
                  <div key={idx} className="timeline-item">
                    <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>Moved to {h.stage}</div>
                    <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>
                      by {h.movedBy} on {new Date(h.movedAt).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "offer" && (
            <div>
              <h4 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Offer & Onboarding Sign-off</h4>
              <p style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>
                When candidate accepts offer and moves to <strong>Hired</strong> stage, an official Employee Account is automatically initialized in the database.
              </p>
              <button className="btn-primary" onClick={() => setShowOfferModal(true)}>
                + Create / View Offer Letter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* HR Assign Team Lead Modal */}
      {showAssignModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-body" style={{ maxWidth: 440, background: "#fff", borderRadius: 14, padding: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.85rem", fontWeight: 700 }}>Assign Candidate to Team Lead</h3>
            <form onSubmit={handleAssignSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Team Lead Name *</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  placeholder="e.g. Lead Engineer Name"
                  value={leadNameInput}
                  onChange={(e) => setLeadNameInput(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Assign Candidate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Lead Recommendation Modal */}
      {showLeadModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-body" style={{ maxWidth: 480, background: "#fff", borderRadius: 14, padding: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.85rem", fontWeight: 700 }}>Submit Team Lead Recommendation</h3>
            <form onSubmit={handleLeadRecSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Recommendation Decision *</label>
                <select
                  className="ats-select"
                  style={{ width: "100%" }}
                  required
                  value={leadRecInput}
                  onChange={(e) => setLeadRecInput(e.target.value)}
                >
                  <option value="Proceed to Technical Interview">✔ Proceed to Technical Interview</option>
                  <option value="Hold">⏳ Hold</option>
                  <option value="Reject">❌ Reject Candidate</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Review Notes & Technical Feedback</label>
                <textarea
                  className="ats-input"
                  style={{ width: "100%", height: 80, resize: "vertical" }}
                  placeholder="Notes on resume fit, technical experience, domain suitability..."
                  value={leadCommentsInput}
                  onChange={(e) => setLeadCommentsInput(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowLeadModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Recommendation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {showOfferModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-body" style={{ maxWidth: 500, background: "#fff", borderRadius: 14, padding: "1.5rem" }}>
            <h3 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Generate Offer Letter</h3>
            <form onSubmit={handleGenerateOffer} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Offered CTC / Annual Salary (INR) *</label>
                <input
                  type="number"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  placeholder="e.g. 1200000"
                  value={offerData.salary}
                  onChange={(e) => setOfferData({ ...offerData, salary: e.target.value })}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Expected Joining Date *</label>
                <input
                  type="date"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  value={offerData.joiningDate}
                  onChange={(e) => setOfferData({ ...offerData, joiningDate: e.target.value })}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Reporting Manager</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="e.g. Team Lead Name"
                  value={offerData.reportingManager}
                  onChange={(e) => setOfferData({ ...offerData, reportingManager: e.target.value })}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowOfferModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Generate & Send Offer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
